"use client";

import type React from "react";
import type { Metadata } from "next";
import { Inter, Schibsted_Grotesk } from "next/font/google";
import "./globals.css";
import { ThemeProvider } from "@/components/theme-provider";
import Navigation from "@/components/navigation";
import { Footer } from "@/components/footer";
import { AuthProvider } from "@/components/auth-provider";
import { usePathname } from "next/navigation";
import Script from "next/script";

const inter = Inter({
  subsets: ["latin"],
  display: "swap",
  preload: true,
  fallback: ["system-ui", "arial"],
});

const schibstedGrotesk = Schibsted_Grotesk({
  subsets: ["latin"],
  weight: "variable",
  display: "swap",
  preload: true,
  fallback: [],
  adjustFontFallback: false,
});

function RootLayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isLoginPage = pathname === "/login";
  const isBuyerDashboard = pathname === "/dashboard/buyer";
  const isSellerDashboard = pathname === "/dashboard/seller";
  const isRoleDashboard = isBuyerDashboard || isSellerDashboard;
  const isRolePage =
    pathname.startsWith("/dashboard/buyer") ||
    pathname.startsWith("/buyer-panel") ||
    pathname.startsWith("/buyer-leaderboard") ||
    pathname.startsWith("/createReverse") ||
    pathname.startsWith("/dashboard/seller") ||
    pathname.startsWith("/seller-panel") ||
    pathname.startsWith("/sellerPanel") ||
    pathname.startsWith("/seller-bid-leader-board");

  return (
    <div
      className={`flex flex-col bg-background text-foreground ${
        isRoleDashboard ? "h-screen overflow-hidden" : "min-h-screen"
      } ${isRolePage ? schibstedGrotesk.className : ""}`}
    >
      {!isLoginPage && !isRoleDashboard && <Navigation />}
      <main
        className={
          isLoginPage
            ? ""
            : isRoleDashboard
              ? "min-h-0 flex-1 overflow-hidden"
              : "flex-1"
        }
      >
        {children}
      </main>
      {!isLoginPage && !isRoleDashboard && <Footer />}
    </div>
  );
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <title>Briskon Auctions - Premium Online Auction Platform</title>
        <meta
          name="description"
          content="Experience the ultimate auction platform with live bidding, VIP access, and premium auction houses. Join Briskon for exclusive auctions and unparalleled service."
        />
      </head>
      <body className={inter.className}>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          enableSystem
          disableTransitionOnChange={false}
          storageKey="briskon-theme"
        >
          <AuthProvider>
            <RootLayoutContent>{children}</RootLayoutContent>
            <div
              id="google_translate_element"
              style={{ display: "none" }}
            ></div>
          </AuthProvider>
        </ThemeProvider>
      </body>
      <Script
        id="google-translate-script"
        src="//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
        strategy="afterInteractive"
      />
      <Script
        id="noupe-chatbot-script"
        src="https://www.noupe.com/embed/019c7a8a91cd74b48b2c7e3e8f0fe71c169a.js"
        strategy="afterInteractive"
      />
      <Script id="google-translate-init" strategy="afterInteractive">
        {`
          function googleTranslateElementInit() {
            new google.translate.TranslateElement({
              pageLanguage: 'en',
              includedLanguages: 'en,fr,es,hi,de,zh,ja,ar',
              autoDisplay: false
            }, 'google_translate_element');
          }

          // Force hide the google translate bar if it appears
          setInterval(function() {
            var bar = document.querySelector('.goog-te-banner-frame');
            if (bar) bar.style.display = 'none';
            var container = document.getElementById(':1.container');
            if (container) container.style.display = 'none';
            document.body.style.top = '0px';
          }, 500);

          // Monkey patch removeChild and insertBefore to handle Google Translate/React conflicts
          (function() {
            const originalRemoveChild = Node.prototype.removeChild;
            Node.prototype.removeChild = function(child) {
              if (child.parentNode !== this) {
                return child;
              }
              return originalRemoveChild.apply(this, arguments);
            };

            const originalInsertBefore = Node.prototype.insertBefore;
            Node.prototype.insertBefore = function(newNode, referenceNode) {
              if (referenceNode && referenceNode.parentNode !== this) {
                return newNode;
              }
              return originalInsertBefore.apply(this, arguments);
            };
          })();
        `}
      </Script>
    </html>
  );
}
